export default function MatchEnd({ data, myKey, playerName, opponentName, onPlayAgain, onLeave }) {
  const { winner, round_wins, total_points, rounds_detail, reason } = data;

  const iWon = winner === myKey;
  const isTie = winner === null;

  const myTotal = myKey === 'p1' ? total_points?.p1 : total_points?.p2;
  const oppTotal = myKey === 'p1' ? total_points?.p2 : total_points?.p1;
  const myWins = myKey === 'p1' ? round_wins?.p1 : round_wins?.p2;
  const oppWins = myKey === 'p1' ? round_wins?.p2 : round_wins?.p1;

  const specialReason = reason === 'disconnect'
    ? '🔌 Opponent disconnected'
    : reason === 'inactivity'
    ? '⏰ Opponent timed out'
    : null;

  return (
    <div style={styles.overlay}>
      <div style={styles.card}>
        {/* Trophy / result */}
        <div style={styles.trophySection}>
          <span style={styles.trophyEmoji}>
            {isTie ? '🤝' : iWon ? '🏆' : '💀'}
          </span>
          <h2
            style={{
              ...styles.resultTitle,
              color: isTie ? '#fff' : iWon ? '#fbbf24' : '#ef4444',
            }}
          >
            {isTie ? "It's a Draw!" : iWon ? 'You Won!' : 'You Lost'}
          </h2>
          {specialReason && <p style={styles.specialReason}>{specialReason}</p>}
        </div>

        {/* Total score bar */}
        <div style={styles.totalScore}>
          <div style={styles.playerScore}>
            <span style={styles.playerName}>You</span>
            <span style={styles.totalPts}>{myTotal || 0}</span>
            <span style={styles.ptsLabel}>pts</span>
          </div>
          <div style={styles.scoreDivider}>
            <span style={styles.roundScore}>{myWins || 0} — {oppWins || 0}</span>
            <span style={styles.roundScoreLabel}>Rounds</span>
          </div>
          <div style={styles.playerScore}>
            <span style={styles.playerName}>{opponentName}</span>
            <span style={styles.totalPts}>{oppTotal || 0}</span>
            <span style={styles.ptsLabel}>pts</span>
          </div>
        </div>

        {/* Round by round breakdown */}
        {rounds_detail?.length > 0 && (
          <div style={styles.roundsSection}>
            <p style={styles.roundsTitle}>Round by Round</p>
            {rounds_detail.map((r) => {
              const myRoundScore = myKey === 'p1' ? r.p1Score : r.p2Score;
              const oppRoundScore = myKey === 'p1' ? r.p2Score : r.p1Score;
              const iWonRound = r.winner === myKey;
              const tiedRound = r.winner === null;
              return (
                <div key={r.round} style={styles.roundRow}>
                  <span style={styles.roundNum}>Round {r.round}</span>
                  <span style={styles.roundCeleb}>{r.celebrity}</span>
                  <div style={styles.roundScores}>
                    <span style={{ color: iWonRound ? '#22c55e' : '#ef4444' }}>
                      {myRoundScore}
                    </span>
                    <span style={styles.vs}>vs</span>
                    <span style={{ color: iWonRound ? '#ef4444' : '#22c55e' }}>
                      {oppRoundScore}
                    </span>
                  </div>
                  <span style={styles.roundResult}>
                    {tiedRound ? '🤝' : iWonRound ? '✅' : '❌'}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* Streak badge */}
        {iWon && (
          <div style={styles.streakBadge}>
            🔥 Win streak updated! Keep playing to earn the 👑 crown badge.
          </div>
        )}

        {/* Actions */}
        <div style={styles.actions}>
          <button style={styles.playAgainBtn} onClick={onPlayAgain}>
            Play Again
          </button>
          <button style={styles.leaveBtn} onClick={onLeave}>
            Leave
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed', inset: 0, zIndex: 200,
    background: 'rgba(0,0,0,0.92)', backdropFilter: 'blur(16px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '20px', overflowY: 'auto',
  },
  card: {
    background: '#0f0f1e', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '28px', padding: '36px 28px',
    maxWidth: '400px', width: '100%',
    display: 'flex', flexDirection: 'column', gap: '20px',
  },
  trophySection: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' },
  trophyEmoji: { fontSize: '56px', lineHeight: 1 },
  resultTitle: {
    margin: 0, fontSize: '32px', fontWeight: '900',
    fontFamily: "'Syne', sans-serif", letterSpacing: '-1px',
  },
  specialReason: { margin: 0, fontSize: '13px', color: 'rgba(255,255,255,0.4)' },
  totalScore: {
    display: 'flex', alignItems: 'center',
    background: 'rgba(255,255,255,0.04)', borderRadius: '16px', padding: '16px',
  },
  playerScore: {
    flex: 1, display: 'flex', flexDirection: 'column',
    alignItems: 'center', gap: '2px',
  },
  playerName: { fontSize: '12px', color: 'rgba(255,255,255,0.45)' },
  totalPts: { fontSize: '36px', fontWeight: '900', color: '#fff', lineHeight: 1 },
  ptsLabel: { fontSize: '11px', color: 'rgba(255,255,255,0.3)' },
  scoreDivider: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px',
    padding: '0 16px',
  },
  roundScore: { fontSize: '22px', fontWeight: '800', color: '#6366f1' },
  roundScoreLabel: { fontSize: '10px', color: 'rgba(255,255,255,0.3)' },
  roundsSection: { display: 'flex', flexDirection: 'column', gap: '8px' },
  roundsTitle: { margin: 0, fontSize: '12px', color: 'rgba(255,255,255,0.35)', fontWeight: '600' },
  roundRow: {
    display: 'flex', alignItems: 'center', gap: '10px',
    padding: '8px 12px', background: 'rgba(255,255,255,0.03)',
    borderRadius: '10px',
  },
  roundNum: { fontSize: '11px', color: 'rgba(255,255,255,0.4)', minWidth: '52px' },
  roundCeleb: { flex: 1, fontSize: '12px', color: 'rgba(255,255,255,0.7)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  roundScores: { display: 'flex', gap: '6px', alignItems: 'center', fontSize: '13px', fontWeight: '700' },
  vs: { color: 'rgba(255,255,255,0.2)', fontSize: '11px' },
  roundResult: { fontSize: '14px' },
  streakBadge: {
    padding: '10px 14px',
    background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.2)',
    borderRadius: '12px', fontSize: '12px', color: '#fbbf24', textAlign: 'center',
  },
  actions: { display: 'flex', gap: '10px' },
  playAgainBtn: {
    flex: 1, padding: '14px',
    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
    border: 'none', borderRadius: '14px', color: '#fff',
    fontSize: '15px', fontWeight: '700', cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
  },
  leaveBtn: {
    padding: '14px 20px',
    background: 'transparent', border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '14px', color: 'rgba(255,255,255,0.5)',
    fontSize: '14px', cursor: 'pointer',
  },
};